import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Views
import LandingView from '@/views/LandingView.vue'
import PackageSelectorView from '@/views/PackageSelectorView.vue'
import CostumeSetPackagesView from '@/views/CostumeSetPackagesView.vue'
import CosmeticPackagesView from '@/views/CosmeticPackagesView.vue'
import PhotographyPackagesView from '@/views/PhotographyPackagesView.vue'
import BookingView from '@/views/BookingView.vue'
import ThankYouView from '@/views/ThankYouView.vue'
import AccessDeniedView from '@/views/AccessDeniedView.vue'
import ErrorView from '@/views/ErrorView.vue'
import LegalPageView from '@/views/LegalPageView.vue'

// Admin Routes
import adminRoutes from '@/admin/router/admin.ts'

const routes = [
  {
    path: '/',
    name: 'landing',
    component: LandingView,
    meta: { title: 'Achiraya Studio - Traditional Thai Photography' }
  },
  {
    path: '/packages',
    name: 'packages',
    component: PackageSelectorView,
    meta: { title: 'Packages - Achiraya Studio' }
  },
  {
    path: '/packages/cosmetic',
    name: 'cosmetic-packages',
    component: CosmeticPackagesView,
    meta: { title: 'Cosmetic Packages - Achiraya Studio' }
  },
  {
    path: '/packages/costume_set',
    name: 'costume-set-packages',
    component: CostumeSetPackagesView,
    meta: { title: 'Costume Set Packages - Achiraya Studio' }
  },
  {
    path: '/packages/photography',
    name: 'photography-packages',
    component: PhotographyPackagesView,
    meta: { title: 'Photography Packages - Achiraya Studio' }
  },
  {
    path: '/booking',
    name: 'booking',
    component: BookingView,
    meta: { title: 'Complete Your Booking - Achiraya Studio' }
  },
  {
    path: '/cart',
    redirect: '/booking'
  },
  {
    path: '/booking/datetime',
    redirect: '/booking'
  },
  {
    path: '/checkout',
    redirect: '/booking'
  },
  {
    path: '/booking/thank-you',
    name: 'thank-you',
    component: ThankYouView,
    meta: { title: 'Thank You - Achiraya Studio' },
    beforeEnter: (to) => {
      if (!to.query.order_id) {
        return { path: '/' }
      }
    }
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: LegalPageView,
    meta: {
      title: 'Privacy Policy - Achiraya Studio',
      legalDocument: 'privacyPolicy'
    }
  },
  {
    path: '/terms-of-use',
    name: 'terms-of-use',
    component: LegalPageView,
    meta: {
      title: 'Terms of Use - Achiraya Studio',
      legalDocument: 'termsOfUse'
    }
  },
  // Admin routes
  ...adminRoutes,
  // Access Denied page (422)
  {
    path: '/access-denied',
    name: 'access-denied',
    component: AccessDeniedView,
    meta: {
      title: 'Access Denied - Achiraya Studio',
      hideLayout: true
    }
  },
  // Error pages
  {
    path: '/error/:code',
    name: 'error',
    component: ErrorView,
    meta: {
      title: 'Error - Achiraya Studio',
      hideLayout: true
    }
  },
  // Legacy error code paths
  {
    path: '/404',
    name: 'not-found-legacy',
    component: ErrorView,
    props: { code: '404' },
    meta: {
      title: 'Page Not Found - Achiraya Studio',
      hideLayout: true
    }
  },
  {
    path: '/500',
    name: 'server-error-legacy',
    component: ErrorView,
    props: { code: '500' },
    meta: {
      title: 'Server Error - Achiraya Studio',
      hideLayout: true
    }
  },
  {
    path: '/422',
    name: 'unprocessable-legacy',
    redirect: { name: 'access-denied' }
  },
  // Catch all - 404
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: ErrorView,
    props: { code: '404' },
    meta: {
      title: 'Page Not Found - Achiraya Studio',
      hideLayout: true
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  // Update page title
  if (to.meta.title) {
    document.title = to.meta.title as string
  }

  const authStore = useAuthStore()

  // Wait for auth initialization if not ready
  if (!authStore.initialized) {
    await authStore.initAuth()
  }

  // Check if route requires staff access (Admin Area)
  if (to.meta.requiresStaff) {
    if (!authStore.isAuthenticated) {
      // Not logged in - store intended destination and redirect to admin login
      localStorage.setItem('redirect_after_login', to.fullPath)
      next({ path: '/admin/login' })
      return
    }

    if (!authStore.isStaff) {
      // Logged in but not staff - redirect to 422 page
      console.log('[Router Guard] Access denied - user is not staff')
      next({ name: 'access-denied' })
      return
    }
  }

  // Check if route requires guest (like admin login)
  if (to.meta.requiresGuest) {
    if (authStore.isAuthenticated && authStore.isStaff) {
      // Already logged in as staff - redirect to admin dashboard
      next({ path: '/admin' })
      return
    }
  }

  // Check if route requires auth (general)
  if (to.meta.requiresAuth) {
    if (!authStore.isAuthenticated) {
      // Store intended destination and redirect to login
      localStorage.setItem('redirect_after_login', to.fullPath)
      // Redirect to home page
      next({ path: '/', query: { login: 'required' } })
      return
    }
  }

  next()
})

export default router
